import React, { useState, useEffect } from "react";
import { User, Edit3, Save, X, Settings, Award, Target, Activity, MapPin, Lightbulb, BarChart3, Package, Cloud, Thermometer, Wind, Zap } from "lucide-react";
import SEO, { PAGE_SEO } from "./SEO";
import { UserProfile as UserProfileType, GolfClub, ClubType, WeatherConditions } from "../types";
import { createDefaultProfile } from "../utils/storage";
import { CLUB_DATA } from "../utils/calculations";
import { analyzeGapping } from "../utils/distanceCalculator";
import { generatePracticeRecommendations } from "../utils/progress";
import { getCurrentWeather, getWeatherRecommendations, getUserLocation } from "../utils/weather";
import { getAvailableTips, getNextTipToUnlock, getShareCount, shareToTwitter, shareToFacebook, copyToClipboard, baseTips, premiumTips } from "../utils/proTips";
import { aiService } from "../services/aiService";
import { Link } from "react-router-dom";
import { ensureReferralCode, getReferralCount } from "../services/profileService";
import { ONBOARDING_QUESTION_COUNT } from "../config/onboarding";
import { stripMarkdown } from "../utils/formatAIText";
import { estimateHandicapFromScore, estimateSwingSpeed, getPersonaFromScore } from "../utils/golfPersona";
import AccountPrivacy from "./AccountPrivacy";

interface UserProfileProps {
  userProfile: UserProfileType | null;
  onProfileUpdate: (profile: UserProfileType) => Promise<boolean> | void;
  onSignOut: () => void;
  onOpenOnboarding?: () => void;
}

const UserProfile: React.FC<UserProfileProps> = ({
  userProfile,
  onProfileUpdate,
  onSignOut,
  onOpenOnboarding,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [isEditingGolfBag, setIsEditingGolfBag] = useState(false);
  const [formData, setFormData] = useState<UserProfileType>(
    userProfile || createDefaultProfile(),
  );
  const [tempGolfBag, setTempGolfBag] = useState<GolfClub[]>([]);
  const [gappingScore, setGappingScore] = useState<number | null>(null);
  const [practiceRecommendations, setPracticeRecommendations] = useState<any[]>([]);
  const [editingClub, setEditingClub] = useState<string | null>(null);
  const [editDistance, setEditDistance] = useState<string>("");
  const [editingLoft, setEditingLoft] = useState<string | null>(null);
  const [editLoftValue, setEditLoftValue] = useState<string>("");
  const [editingClubDetails, setEditingClubDetails] = useState<string | null>(null);
  const [editClubBrand, setEditClubBrand] = useState<string>("");
  const [editClubModel, setEditClubModel] = useState<string>("");
  const [editClubShaftType, setEditClubShaftType] = useState<string>("");
  const [editClubShaftFlex, setEditClubShaftFlex] = useState<string>("");
  const [editClubGripType, setEditClubGripType] = useState<string>("");
  const [weather, setWeather] = useState<WeatherConditions | null>(null);
  const [weatherRecommendations, setWeatherRecommendations] = useState<string[]>([]);
  const [proTips, setProTips] = useState<any[]>([]);
  const [shareCount, setShareCount] = useState<number>(0);
  const [currentTipIndex, setCurrentTipIndex] = useState<number>(0);
  const [isLoadingLocation, setIsLoadingLocation] = useState<boolean>(false);
  const [dailyTip, setDailyTip] = useState<string | null>(null);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [isLoadingDailyTip, setIsLoadingDailyTip] = useState<boolean>(false);
  const [referralCode, setReferralCode] = useState<string | null>(null);
  const [referralCount, setReferralCount] = useState<number>(0);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [isLoadingReferral, setIsLoadingReferral] = useState<boolean>(false);
  const [referralCopied, setReferralCopied] = useState<boolean>(false);

  useEffect(() => {
    // AGGRESSIVE RECOVERY: If no profile data, try to recover from localStorage
    if (!userProfile || !userProfile.id) {
      console.log('🔧 UserProfile: No profile data, attempting aggressive recovery...');
      
      // Try to recover from localStorage
      const backupKeys = [
        'PERSISTENT_PROFILE_',
        'quizBackup_',
        'onboarding_',
        'emergency_backup_',
        'clubiq-user-profile'
      ];
      
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && backupKeys.some(prefix => key.includes(prefix) || key === 'clubiq-user-profile')) {
          try {
            const data = JSON.parse(localStorage.getItem(key) || '{}');
            if (data && (data.golfBag || data.profile?.golfBag)) {
              console.log(`✅ Found backup in ${key}, using as formData`);
              const recovered = {
                id: data.id || data.userId || '',
                firstName: data.firstName || data.profile?.firstName || 'User',
                lastName: data.lastName || data.profile?.lastName || 'Name',
                email: data.email || data.profile?.email || '',
                handicap: data.handicap ?? data.profile?.handicap ?? 18,
                height: data.height ?? data.profile?.height ?? 70,
                weight: data.weight ?? data.profile?.weight ?? 180,
                age: data.age ?? data.profile?.age ?? 35,
                swingSpeed: data.swingSpeed || data.profile?.swingSpeed,
                swingSpeedRange: data.swingSpeedRange || data.profile?.swingSpeedRange,
                swingTempo: data.swingTempo || data.profile?.swingTempo || 'medium',
                ballFlight: data.ballFlight || data.profile?.ballFlight || 'straight',
                skillLevel: data.skillLevel || data.profile?.skillLevel || 'intermediate',
                playingExperience: data.playingExperience || data.profile?.playingExperience || 'casual',
                courseFrequency: data.courseFrequency || data.profile?.courseFrequency || 'monthly',
                missPattern: data.missPattern || data.profile?.missPattern,
                preferredCourseType: data.preferredCourseType || data.profile?.preferredCourseType,
                golfGoals: data.golfGoals || data.profile?.golfGoals || [],
                currentIssues: data.currentIssues || data.profile?.currentIssues || [],
                clubDistances: data.clubDistances || data.profile?.clubDistances || [],
                golfBag: data.golfBag || data.profile?.golfBag || [],
                preferredUnits: data.preferredUnits || data.profile?.preferredUnits || 'yards',
                hasCompletedOnboarding: data.hasCompletedOnboarding ?? data.profile?.hasCompletedOnboarding ?? false,
                averageScore: data.averageScore || data.profile?.averageScore,
                golfPersona: data.golfPersona || data.profile?.golfPersona,
                createdAt: new Date(),
                updatedAt: new Date(),
              };
              setFormData(recovered as UserProfileType);
              console.log('✅ Recovered profile applied to formData');
              break;
            }
          } catch (e) {
            console.error(`Failed to parse ${key}:`, e);
          }
        }
      }
    }
    
    // Update formData when userProfile changes
    if (userProfile) {
      console.log('👤 UserProfile updating formData from profile:', {
        height: userProfile.height,
        weight: userProfile.weight,
        age: userProfile.age,
        golfBagLength: userProfile.golfBag?.length || 0
      });
      setFormData(userProfile);
    }
    
    // Calculate gapping score when user profile changes
    if (userProfile && userProfile.golfBag && userProfile.golfBag.length > 0) {
      const gapAnalysis = analyzeGapping(userProfile.golfBag);
      setGappingScore(gapAnalysis.gappingScore);
    }

    // Load practice recommendations
    if (userProfile) {
      const recommendations = generatePracticeRecommendations(userProfile);
      setPracticeRecommendations(Array.isArray(recommendations) ? recommendations.slice(0, 3) : []);
    }

    // Load weather data
    getCurrentWeather().then((weatherData) => {
      setWeather(weatherData);
      const weatherRecs = getWeatherRecommendations(weatherData);
      setWeatherRecommendations(Array.isArray(weatherRecs) ? weatherRecs : []);
    });

    // Load pro tips
    const tips = getAvailableTips();
    const unlockedTips = tips.filter(tip => tip.isUnlocked);
    setProTips(unlockedTips);
    setShareCount(getShareCount());
    
    if (currentTipIndex >= unlockedTips.length) {
      setCurrentTipIndex(0);
    }

    // Load daily AI tip
    if (userProfile) {
      setIsLoadingDailyTip(true);
      getCurrentWeather().then((weatherData) => {
        aiService.getDailyGolfTip({
          userProfile,
          weather: weatherData
        }).then((tip) => {
          setDailyTip(tip);
          setIsLoadingDailyTip(false);
        }).catch((error) => {
          console.error('Failed to load daily tip:', error);
          setIsLoadingDailyTip(false);
        });
      });

      // Load referral code and count
      setIsLoadingReferral(true);
      
      // First check if user already has a code in their profile
      if (userProfile.referralCode) {
        setReferralCode(userProfile.referralCode);
      } else if (userProfile.id && userProfile.firstName) {
        // Generate one if they don't have one yet
        ensureReferralCode(userProfile.id, userProfile.firstName)
          .then((code) => {
            setReferralCode(code);
          })
          .catch((error) => {
            console.error('Failed to get referral code:', error);
          });
      }
      
      // Get referral count
      if (userProfile.id) {
        getReferralCount(userProfile.id)
          .then((count) => {
            setReferralCount(count);
            setIsLoadingReferral(false);
          })
          .catch((error) => {
            console.error('Failed to get referral count:', error);
            setIsLoadingReferral(false);
          });
      } else {
        setIsLoadingReferral(false);
      }
    }
  }, [userProfile, currentTipIndex]);

  const handleInputChange = (field: keyof UserProfileType, value: any) => {
    setFormData((prev) => {
      const updated = { ...prev, [field]: value };
      
      // Reactive derived data: when source fields change, recalculate dependent fields
      // This keeps handicap, swing speed, and persona in sync automatically.
      
      if (field === 'averageScore' && typeof value === 'number' && value > 0) {
        // Average score changed → recalculate handicap, persona, and swing speed
        updated.handicap = estimateHandicapFromScore(value);
        updated.golfPersona = getPersonaFromScore(value).id;
        updated.swingSpeed = estimateSwingSpeed(value, prev.age || 35);
        updated.skillLevel = getPersonaFromScore(value).skillLevel as any;
      }
      
      if (field === 'age' && typeof value === 'number' && value > 0 && prev.averageScore) {
        // Age changed → recalculate swing speed (it's age-dependent)
        updated.swingSpeed = estimateSwingSpeed(prev.averageScore, value);
      }
      
      return updated;
    });
  };

  const handleSave = async () => {
    console.log('💾 Saving profile changes...', {
      handicap: formData.handicap,
      height: formData.height,
      weight: formData.weight
    });
    const success = await onProfileUpdate(formData);
    
    if (success !== false) {
      console.log('✅ Profile saved successfully');
      setIsEditing(false);
    } else {
      // Keep editing mode open if save failed
      console.error('❌ Profile save failed - keeping edit mode open');
    }
  };

  const handleCancel = () => {
    setFormData(userProfile || createDefaultProfile());
    setIsEditing(false);
  };

  const handleStartGolfBagEdit = () => {
    setTempGolfBag([...formData.golfBag]);
    setIsEditingGolfBag(true);
  };

  const handleSaveGolfBag = async () => {
    if (!userProfile) return;
    
    const updatedProfile = {
      ...userProfile,
      golfBag: tempGolfBag,
      updatedAt: new Date()
    };
    
    console.log('💾 Saving golf bag changes...', {
      clubCount: tempGolfBag.length
    });
    
    // Save to Supabase via App.tsx handler
    const success = await onProfileUpdate(updatedProfile);
    
    if (success !== false) {
      console.log('✅ Golf bag saved successfully');
      setFormData(updatedProfile);
      setIsEditingGolfBag(false);
    } else {
      console.error('❌ Golf bag save failed - keeping edit mode open');
    }
  };

  const handleCancelGolfBag = () => {
    setTempGolfBag([...formData.golfBag]);
    setIsEditingGolfBag(false);
  };

  const toggleClubInBag = (clubType: string) => {
    const clubData = CLUB_DATA[clubType as ClubType];
    if (!clubData) return;

    const existingIndex = tempGolfBag.findIndex((c) => c.type === clubType);

    if (existingIndex >= 0) {
      // Remove club from bag
      setTempGolfBag((prev) => prev.filter((_, index) => index !== existingIndex));
    } else {
      // Add club to bag
      if (tempGolfBag.length < 13) {
        const newClub: GolfClub = {
          id: `club-${clubType}-${Date.now()}`,
          type: clubType as ClubType,
          brand: "Standard",
          model: clubType.charAt(0).toUpperCase() + clubType.slice(1).replace('-', ' '),
          shaftType: "graphite",
          shaftFlex: "regular",
          lie: 60,
          loft: clubData.loft,
          length: 36,
          isInBag: true,
          distance: undefined,
          isDistanceEstimated: true,
          notes: "",
        };
        setTempGolfBag((prev) => [...prev, newClub]);
      }
    }
  };

  const handleDistanceEdit = (clubId: string, currentDistance?: number) => {
    setEditingClub(clubId);
    setEditDistance(currentDistance?.toString() || "");
  };

  const saveDistanceEdit = async () => {
    if (!userProfile || !editingClub) return;

    const distance = parseInt(editDistance);
    if (isNaN(distance) || distance <= 0) {
      alert('Please enter a valid distance greater than 0');
      return;
    }

    const editingClubData = userProfile.golfBag.find(c => c.id === editingClub);
    const clubName = editingClubData?.type?.toUpperCase().replace('-', ' ') || 'Club';

    const updatedGolfBag = userProfile.golfBag.map(club =>
      club.id === editingClub
        ? { 
            ...club, 
            distance, 
            isDistanceEstimated: false,
            isManuallySet: true  // CRITICAL: Mark as manually set to prevent AI overwrite
          }
        : club
    );

    console.log(`💾 Saving ${clubName} distance: ${distance} yards (isManuallySet: true)`);

    const success = await onProfileUpdate({
      ...userProfile,
      golfBag: updatedGolfBag
    });

    if (success !== false) {
      console.log(`✅ ${clubName} distance saved successfully`);
    }

    setEditingClub(null);
    setEditDistance("");
  };

  const cancelDistanceEdit = () => {
    setEditingClub(null);
    setEditDistance("");
  };

  const handleLoftEdit = (clubId: string, currentLoft: number) => {
    setEditingLoft(clubId);
    setEditLoftValue(currentLoft.toString());
    // Scroll to top so modal is visible on mobile
    setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 100);
  };

  const saveLoftEdit = async () => {
    if (!userProfile || !editingLoft) return;

    const loft = parseFloat(editLoftValue);
    if (isNaN(loft) || loft <= 0 || loft > 90) {
      alert('Please enter a valid loft between 1 and 90 degrees');
      return;
    }

    const editingClubData = userProfile.golfBag.find(c => c.id === editingLoft);
    const clubName = editingClubData?.type?.toUpperCase().replace('-', ' ') || 'Club';

    const updatedGolfBag = userProfile.golfBag.map(club =>
      club.id === editingLoft
        ? { ...club, loft }
        : club
    );

    console.log(`💾 Saving ${clubName} loft: ${loft}°`);

    const success = await onProfileUpdate({
      ...userProfile,
      golfBag: updatedGolfBag
    });

    if (success !== false) {
      console.log(`✅ ${clubName} loft saved successfully`);
    }

    setEditingLoft(null);
    setEditLoftValue("");
  };

  const cancelLoftEdit = () => {
    setEditingLoft(null);
    setEditLoftValue("");
  };

  const handleClubDetailsEdit = (club: GolfClub) => {
    setEditingClubDetails(club.id);
    setEditClubBrand(club.brand || "");
    setEditClubModel(club.model || "");
    setEditClubShaftType(club.shaftType || "steel");
    setEditClubShaftFlex(club.shaftFlex || "regular");
    setEditClubGripType((club as any).gripType || "standard");
    // Scroll to top for mobile
    setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 100);
  };

  const saveClubDetailsEdit = async () => {
    if (!userProfile || !editingClubDetails) return;

    const editingClubData = userProfile.golfBag.find(c => c.id === editingClubDetails);
    const clubName = editingClubData?.type?.toUpperCase().replace('-', ' ') || 'Club';

    const updatedGolfBag = userProfile.golfBag.map(club =>
      club.id === editingClubDetails
        ? { 
            ...club, 
            brand: editClubBrand,
            model: editClubModel,
            shaftType: editClubShaftType as "graphite" | "steel",
            shaftFlex: editClubShaftFlex as "extra-stiff" | "stiff" | "regular" | "senior" | "ladies",
            gripType: editClubGripType as "standard" | "midsize" | "jumbo" | "undersize" | "corded" | "wrap"
          }
        : club
    );

    console.log(`💾 Saving ${clubName} details: ${editClubBrand} ${editClubModel}`);

    const success = await onProfileUpdate({
      ...userProfile,
      golfBag: updatedGolfBag
    });

    if (success !== false) {
      console.log(`✅ ${clubName} details saved successfully`);
      setEditingClubDetails(null);
      setEditClubBrand("");
      setEditClubModel("");
      setEditClubShaftType("");
      setEditClubShaftFlex("");
      setEditClubGripType("");
    } else {
      console.error(`❌ ${clubName} details save failed`);
    }
  };

  const cancelClubDetailsEdit = () => {
    setEditingClubDetails(null);
    setEditClubBrand("");
    setEditClubModel("");
    setEditClubShaftType("");
    setEditClubShaftFlex("");
    setEditClubGripType("");
  };

  // Debug logging
  console.log('UserProfile render - userProfile:', userProfile);
  console.log('UserProfile render - formData:', formData);
  console.log('UserProfile render - userProfile.golfBag:', userProfile?.golfBag);

  // Show loading state while profile is being loaded
  // FIXED: Use formData as fallback - it will have recovered data from localStorage
  const profileData = userProfile || formData;
  
  // Only show loading if we have absolutely no data AND no firstName (meaning recovery failed)
  if (!profileData || (!profileData.id && !profileData.firstName)) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="bg-white rounded-2xl shadow-lg p-8 text-center">
          <User className="w-12 h-12 text-gray-400 mx-auto mb-4 animate-pulse" />
          <h2 className="text-xl font-bold text-gray-900 mb-2">Loading Profile...</h2>
          <p className="text-gray-600 mb-4">Please wait while we load your profile data.</p>
          <div className="flex justify-center gap-3 mt-6">
            {onOpenOnboarding && (
              <button
                onClick={onOpenOnboarding}
                className="inline-block bg-green-600 text-white px-6 py-3 rounded-lg hover:bg-green-700 transition-colors"
              >
                Complete Onboarding Quiz
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <SEO {...PAGE_SEO.profile} url="/profile" />
    <div className="max-w-6xl mx-auto p-0 sm:p-6 space-y-0 sm:space-y-6">
      {/* Header - App-Style on Mobile */}
      <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-none sm:rounded-2xl p-4 md:p-6 text-white">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="flex items-center space-x-3 md:space-x-4">
            <div className="w-12 h-12 md:w-16 md:h-16 bg-white/20 rounded-full flex items-center justify-center flex-shrink-0">
              <User className="w-6 h-6 md:w-8 md:h-8" />
            </div>
            <div className="min-w-0">
              <h1 className="text-xl md:text-2xl font-bold truncate">
                {formData.firstName} {formData.lastName}
              </h1>
              {userProfile?.hasCompletedOnboarding ? (
                <>
                  <p className="text-blue-100 text-sm md:text-base">
                    {formData.skillLevel} • {formData.handicap} HCP • Plays {formData.courseFrequency}
                  </p>
                  {formData.averageScore && (
                    <p className="text-blue-200 text-xs md:text-sm">
                      Avg Score: {formData.averageScore} • {formData.golfPersona ? '🎯 Persona Active' : ''}
                    </p>
                  )}
                </>
              ) : (
                <p className="text-blue-100 text-sm md:text-base">
                  Complete your golf profile to get started
                </p>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {!isEditing ? (
              <>
                <button
                  onClick={() => {
                    setIsEditing(true);
                    // Scroll to edit section after a brief delay for state update
                    setTimeout(() => {
                      const editSection = document.getElementById('profile-edit-section');
                      if (editSection) {
                        editSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
                      }
                    }, 100);
                  }}
                  className="flex items-center space-x-1 md:space-x-2 px-3 md:px-4 py-2 bg-white/20 hover:bg-white/30 rounded-lg transition-colors text-xs md:text-base whitespace-nowrap"
                >
                  <Edit3 className="w-4 h-4" />
                  <span>Edit Profile</span>
                </button>
                {onOpenOnboarding && (
                  <button
                    onClick={onOpenOnboarding}
                    className="flex items-center space-x-1 md:space-x-2 px-3 md:px-4 py-2 bg-green-600 hover:bg-green-700 rounded-lg transition-colors text-xs md:text-base whitespace-nowrap"
                  >
                    <Activity className="w-4 h-4" />
                    <span>Retake Quiz</span>
                  </button>
                )}
                {userProfile?.id && (
                  <button
                    onClick={() => {
                      localStorage.removeItem(`hasSeenWalkthrough_${userProfile.id}`);
                      window.location.reload();
                    }}
                    className="flex items-center space-x-1 md:space-x-2 px-3 md:px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors text-xs md:text-base whitespace-nowrap"
                  >
                    <Lightbulb className="w-4 h-4" />
                    <span>Retake Tour</span>
                  </button>
                )}
              </>
            ) : (
              <div className="flex gap-2">
                <button
                  onClick={handleSave}
                  className="flex items-center space-x-1 md:space-x-2 px-3 md:px-4 py-2 bg-green-600 hover:bg-green-700 rounded-lg transition-colors text-xs md:text-base"
                >
                  <Save className="w-4 h-4" />
                  <span>Save</span>
                </button>
                <button
                  onClick={handleCancel}
                  className="flex items-center space-x-1 md:space-x-2 px-3 md:px-4 py-2 bg-white/20 hover:bg-white/30 rounded-lg transition-colors text-xs md:text-base"
                >
                  <X className="w-4 h-4" />
                  <span>Cancel</span>
                </button>
              </div>
            )}
            {/* Sign Out available in navigation menu */}
          </div>
        </div>
      </div>

      {/* Stats Cards - Prominent display */}
      {userProfile && (
        <div className="grid grid-cols-3 gap-3 md:gap-4 mb-6">
          {[
            { 
              label: "Handicap", 
              value: userProfile?.handicap?.toString() || "--", 
              icon: Award, 
              color: "text-emerald-600",
              bgGradient: "from-emerald-50 to-green-50",
              borderColor: "border-emerald-200"
            },
            { 
              label: "Gap Score", 
              value: gappingScore !== null ? `${gappingScore}` : "--", 
              icon: Target, 
              color: "text-blue-600",
              bgGradient: "from-blue-50 to-indigo-50",
              borderColor: "border-blue-200"
            },
            { 
              label: "Clubs", 
              value: `${userProfile?.golfBag?.length || 0}/14`, 
              icon: MapPin, 
              color: "text-purple-600",
              bgGradient: "from-purple-50 to-violet-50",
              borderColor: "border-purple-200"
            },
          ].map((stat, index) => {
            const Icon = stat.icon;
            return (
              <div 
                key={index} 
                className={`bg-gradient-to-br ${stat.bgGradient} rounded-xl p-3 md:p-4 border ${stat.borderColor} shadow-sm`}
              >
                <div className="flex items-center gap-2 mb-1">
                  <Icon size={16} className={stat.color} />
                  <span className="text-gray-500 text-xs md:text-sm font-medium">{stat.label}</span>
                </div>
                <div className={`font-bold text-xl md:text-2xl ${stat.color}`}>
                  {stat.value}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* MAIN FEATURES - Hero-sized Quick Links */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-8">
        <Link
          to="/my-golf-bag"
          className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-green-500 via-emerald-500 to-teal-600 p-8 md:p-10 shadow-2xl hover:shadow-green-500/30 transition-all duration-300 group hover:scale-[1.02] min-h-[200px] md:min-h-[240px]"
        >
          <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent"></div>
          <div className="absolute -right-8 -bottom-8 opacity-10">
            <BarChart3 className="w-48 h-48 text-white" />
          </div>
          <div className="relative z-10 h-full flex flex-col">
            <div className="flex items-start justify-between mb-6">
              <div className="bg-white/25 backdrop-blur-sm p-4 rounded-2xl shadow-lg">
                <BarChart3 className="w-10 h-10 md:w-12 md:h-12 text-white" />
              </div>
              <span className="bg-white/25 backdrop-blur-sm px-4 py-2 rounded-full text-sm text-white font-bold shadow-lg">
                {userProfile?.golfBag?.length || 0} / 14 clubs
              </span>
            </div>
            <div className="flex-1">
              <h3 className="font-bold text-2xl md:text-3xl text-white mb-3">My Golf Bag</h3>
              <p className="text-white/90 text-base md:text-lg leading-relaxed">
                View your clubs, track distances, and get real-time shot recommendations
              </p>
            </div>
            <div className="mt-6 flex items-center text-white text-base font-bold group-hover:translate-x-3 transition-transform">
              Open Your Bag <span className="ml-2 text-xl">→</span>
            </div>
          </div>
        </Link>
        
        <Link
          to="/club-optimizer"
          className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-purple-500 via-violet-500 to-indigo-600 p-8 md:p-10 shadow-2xl hover:shadow-purple-500/30 transition-all duration-300 group hover:scale-[1.02] min-h-[200px] md:min-h-[240px]"
        >
          <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent"></div>
          <div className="absolute -right-8 -bottom-8 opacity-10">
            <Settings className="w-48 h-48 text-white" />
          </div>
          <div className="relative z-10 h-full flex flex-col">
            <div className="flex items-start justify-between mb-6">
              <div className="bg-white/25 backdrop-blur-sm p-4 rounded-2xl shadow-lg">
                <Settings className="w-10 h-10 md:w-12 md:h-12 text-white" />
              </div>
              <span className="bg-white/25 backdrop-blur-sm px-4 py-2 rounded-full text-sm text-white font-bold shadow-lg flex items-center">
                <Zap className="w-4 h-4 mr-1" /> AI-Powered
              </span>
            </div>
            <div className="flex-1">
              <h3 className="font-bold text-2xl md:text-3xl text-white mb-3">Club Optimizer</h3>
              <p className="text-white/90 text-base md:text-lg leading-relaxed">
                Get personalized equipment recommendations and fitting analysis
              </p>
            </div>
            <div className="mt-6 flex items-center text-white text-base font-bold group-hover:translate-x-3 transition-transform">
              Optimize Now <span className="ml-2 text-xl">→</span>
            </div>
          </div>
        </Link>
      </div>

      {/* Main Content Grid - Premium Redesign */}
      <div id="profile-edit-section" className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
        {/* Left Column - Personal Information - PREMIUM */}
        <div className="bg-white rounded-2xl shadow-lg overflow-hidden border border-gray-100">
          {/* Gradient Header */}
          <div className="bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 px-5 py-4">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center">
                <User className="w-5 h-5 text-white" />
              </div>
              <h3 className="text-lg font-bold text-white">Personal Information</h3>
            </div>
          </div>
          
          {/* Content */}
          <div className="p-5 space-y-1">
            {/* Name Row */}
            <div className="grid grid-cols-2 gap-4">
              <div className="group p-3 rounded-xl hover:bg-gray-50 transition-colors">
                <div className="flex items-center space-x-2 mb-1">
                  <div className="w-6 h-6 rounded-lg bg-blue-100 flex items-center justify-center">
                    <User className="w-3.5 h-3.5 text-blue-600" />
                  </div>
                  <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">First Name</span>
                </div>
                {isEditing ? (
                  <input
                    type="text"
                    value={formData.firstName}
                    onChange={(e) => handleInputChange('firstName', e.target.value)}
                    className="w-full px-3 py-2.5 bg-gray-50 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 focus:bg-white transition-all font-medium"
                  />
                ) : (
                  <p className="text-lg font-semibold text-gray-900 mt-1">{formData.firstName}</p>
                )}
              </div>

              <div className="group p-3 rounded-xl hover:bg-gray-50 transition-colors">
                <div className="flex items-center space-x-2 mb-1">
                  <div className="w-6 h-6 rounded-lg bg-blue-100 flex items-center justify-center">
                    <User className="w-3.5 h-3.5 text-blue-600" />
                  </div>
                  <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Last Name</span>
                </div>
                {isEditing ? (
                  <input
                    type="text"
                    value={formData.lastName}
                    onChange={(e) => handleInputChange('lastName', e.target.value)}
                    className="w-full px-3 py-2.5 bg-gray-50 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 focus:bg-white transition-all font-medium"
                  />
                ) : (
                  <p className="text-lg font-semibold text-gray-900 mt-1">{formData.lastName}</p>
                )}
              </div>
            </div>

            {/* Divider */}
            <div className="border-b border-gray-100 my-2"></div>

            {/* Email Row */}
            <div className="group p-3 rounded-xl hover:bg-gray-50 transition-colors">
              <div className="flex items-center space-x-2 mb-1">
                <div className="w-6 h-6 rounded-lg bg-indigo-100 flex items-center justify-center">
                  <svg className="w-3.5 h-3.5 text-indigo-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                </div>
                <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Email</span>
              </div>
              {isEditing ? (
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  className="w-full px-3 py-2.5 bg-gray-50 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 focus:bg-white transition-all font-medium"
                />
              ) : (
                <p className="text-base font-medium text-gray-900 mt-1">{formData.email}</p>
              )}
            </div>

            {/* Divider */}
            <div className="border-b border-gray-100 my-2"></div>

            {/* Stats Row - Clean horizontal layout */}
            <div className="flex items-stretch gap-2 sm:gap-3 bg-gradient-to-r from-gray-50 to-slate-50 rounded-xl p-3 sm:p-4">
              <div className="flex-1 text-center border-r border-gray-200 pr-2 sm:pr-3">
                <div className="w-10 h-10 sm:w-12 sm:h-12 mx-auto rounded-xl bg-gradient-to-br from-purple-500 to-indigo-600 flex items-center justify-center shadow-lg shadow-purple-500/20 mb-2">
                  <svg className="w-5 h-5 sm:w-6 sm:h-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                </div>
                <span className="text-xs font-medium text-gray-500 uppercase tracking-wide block">Age</span>
                {isEditing ? (
                  <input
                    type="number"
                    value={formData.age}
                    onChange={(e) => handleInputChange('age', parseInt(e.target.value))}
                    className="w-16 mx-auto px-2 py-1.5 mt-1 bg-white border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-center font-bold text-xl"
                  />
                ) : (
                  <p className="text-2xl sm:text-3xl font-bold text-gray-900 mt-1">{formData.age}</p>
                )}
              </div>

              <div className="flex-1 text-center border-r border-gray-200 pr-2 sm:pr-3">
                <div className="w-10 h-10 sm:w-12 sm:h-12 mx-auto rounded-xl bg-gradient-to-br from-green-500 to-emerald-600 flex items-center justify-center shadow-lg shadow-green-500/20 mb-2">
                  <svg className="w-5 h-5 sm:w-6 sm:h-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21l4-4m0 0l4 4m-4-4V3" transform="rotate(180 12 12)" />
                  </svg>
                </div>
                <span className="text-xs font-medium text-gray-500 uppercase tracking-wide block">Height</span>
                {isEditing ? (
                  <div className="flex gap-1 mt-1 justify-center">
                    <select
                      value={Math.floor(formData.height / 12)}
                      onChange={(e) => {
                        const feet = parseInt(e.target.value);
                        const inches = formData.height % 12;
                        handleInputChange('height', feet * 12 + inches);
                      }}
                      className="w-14 px-1 py-1.5 bg-white border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 text-center font-bold"
                    >
                      {[4, 5, 6, 7].map(ft => (
                        <option key={ft} value={ft}>{ft}'</option>
                      ))}
                    </select>
                    <select
                      value={formData.height % 12}
                      onChange={(e) => {
                        const feet = Math.floor(formData.height / 12);
                        const inches = parseInt(e.target.value);
                        handleInputChange('height', feet * 12 + inches);
                      }}
                      className="w-14 px-1 py-1.5 bg-white border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 text-center font-bold"
                    >
                      {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11].map(inch => (
                        <option key={inch} value={inch}>{inch}"</option>
                      ))}
                    </select>
                  </div>
                ) : (
                  <p className="text-2xl sm:text-3xl font-bold text-gray-900 mt-1">{Math.floor(formData.height / 12)}'{formData.height % 12}"</p>
                )}
              </div>

              <div className="flex-1 text-center">
                <div className="w-10 h-10 sm:w-12 sm:h-12 mx-auto rounded-xl bg-gradient-to-br from-orange-500 to-red-500 flex items-center justify-center shadow-lg shadow-orange-500/20 mb-2">
                  <svg className="w-5 h-5 sm:w-6 sm:h-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 6l3 1m0 0l-3 9a5.002 5.002 0 006.001 0M6 7l3 9M6 7l6-2m6 2l3-1m-3 1l-3 9a5.002 5.002 0 006.001 0M18 7l3 9m-3-9l-6-2m0-2v2m0 16V5m0 16H9m3 0h3" />
                  </svg>
                </div>
                <span className="text-xs font-medium text-gray-500 uppercase tracking-wide block">Weight</span>
                {isEditing ? (
                  <input
                    type="number"
                    value={formData.weight}
                    onChange={(e) => handleInputChange('weight', parseInt(e.target.value))}
                    className="w-20 mx-auto px-2 py-1.5 mt-1 bg-white border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-center font-bold text-xl"
                  />
                ) : (
                  <p className="text-2xl sm:text-3xl font-bold text-gray-900 mt-1">{formData.weight}<span className="text-sm font-medium text-gray-400 ml-0.5">lb</span></p>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Right Column - Golf Profile - PREMIUM */}
        <div className="bg-white rounded-2xl shadow-lg overflow-hidden border border-gray-100">
          {/* Gradient Header */}
          <div className="bg-gradient-to-r from-green-600 via-emerald-600 to-teal-600 px-5 py-4">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center">
                <Target className="w-5 h-5 text-white" />
              </div>
              <h3 className="text-lg font-bold text-white">Golf Profile</h3>
            </div>
          </div>

          {!userProfile?.hasCompletedOnboarding ? (
            <div className="text-center py-10 px-6">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-green-500 to-emerald-600 rounded-2xl mb-4 shadow-lg shadow-green-500/30">
                <Activity className="w-8 h-8 text-white" />
              </div>
              <p className="text-gray-600 mb-4">Complete the golf profile quiz to see your personalized data</p>
              {onOpenOnboarding && (
                <button
                  onClick={onOpenOnboarding}
                  className="px-6 py-3 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-xl hover:shadow-lg hover:shadow-green-500/30 transition-all font-semibold"
                >
                  Complete Setup
                </button>
              )}
            </div>
          ) : (
            <div className="p-5 space-y-1">
              {/* Handicap - Featured */}
              <div className="group p-4 rounded-xl bg-gradient-to-r from-green-50 to-emerald-50 border border-green-100 mb-3">
                <div className="flex items-center space-x-4">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-green-500 to-emerald-600 flex items-center justify-center shadow-lg shadow-green-500/20 flex-shrink-0">
                    <Award className="w-6 h-6 text-white" />
                  </div>
                  <div className="flex-1">
                    <span className="text-xs font-medium text-green-700 uppercase tracking-wide block mb-1">Handicap Index</span>
                    {isEditing ? (
                      <input
                        type="number"
                        step="0.1"
                        value={formData.handicap}
                        onChange={(e) => handleInputChange('handicap', parseFloat(e.target.value))}
                        className="w-24 px-3 py-1.5 bg-white border-2 border-green-200 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 font-bold text-2xl"
                      />
                    ) : (
                      <p className="text-3xl font-bold text-gray-900">{formData.handicap}</p>
                    )}
                  </div>
                </div>
              </div>

              {/* Two Column Grid */}
              <div className="grid grid-cols-2 gap-3">
                <div className="group p-3 rounded-xl hover:bg-gray-50 transition-colors">
                  <div className="flex items-center space-x-2 mb-1.5">
                    <div className="w-6 h-6 rounded-lg bg-blue-100 flex items-center justify-center">
                      <BarChart3 className="w-3.5 h-3.5 text-blue-600" />
                    </div>
                    <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Skill Level</span>
                  </div>
                  {isEditing ? (
                    <select
                      value={formData.skillLevel}
                      onChange={(e) => handleInputChange('skillLevel', e.target.value)}
                      className="w-full px-3 py-2 bg-gray-50 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 font-medium"
                    >
                      <option value="beginner">Beginner</option>
                      <option value="intermediate">Intermediate</option>
                      <option value="advanced">Advanced</option>
                    </select>
                  ) : (
                    <p className="text-base font-semibold text-gray-900 capitalize mt-1">{formData.skillLevel}</p>
                  )}
                </div>

                <div className="group p-3 rounded-xl hover:bg-gray-50 transition-colors">
                  <div className="flex items-center space-x-2 mb-1.5">
                    <div className="w-6 h-6 rounded-lg bg-purple-100 flex items-center justify-center">
                      <MapPin className="w-3.5 h-3.5 text-purple-600" />
                    </div>
                    <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Frequency</span>
                  </div>
                  {isEditing ? (
                    <select
                      value={formData.courseFrequency}
                      onChange={(e) => handleInputChange('courseFrequency', e.target.value)}
                      className="w-full px-3 py-2 bg-gray-50 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 font-medium"
                    >
                      <option value="rarely">Rarely</option>
                      <option value="monthly">Monthly</option>
                      <option value="weekly">Weekly</option>
                      <option value="multiple">Multiple times/week</option>
                    </select>
                  ) : (
                    <p className="text-base font-semibold text-gray-900 capitalize mt-1">{formData.courseFrequency}</p>
                  )}
                </div>

                <div className="group p-3 rounded-xl hover:bg-gray-50 transition-colors">
                  <div className="flex items-center space-x-2 mb-1.5">
                    <div className="w-6 h-6 rounded-lg bg-amber-100 flex items-center justify-center">
                      <Activity className="w-3.5 h-3.5 text-amber-600" />
                    </div>
                    <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Ball Flight</span>
                  </div>
                  {isEditing ? (
                    <select
                      value={formData.ballFlight}
                      onChange={(e) => handleInputChange('ballFlight', e.target.value)}
                      className="w-full px-3 py-2 bg-gray-50 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 font-medium"
                    >
                      <option value="straight">Straight</option>
                      <option value="fade">Fade</option>
                      <option value="draw">Draw</option>
                      <option value="slice">Slice</option>
                      <option value="hook">Hook</option>
                    </select>
                  ) : (
                    <p className="text-base font-semibold text-gray-900 capitalize mt-1">{formData.ballFlight}</p>
                  )}
                </div>

                <div className="group p-3 rounded-xl hover:bg-gray-50 transition-colors">
                  <div className="flex items-center space-x-2 mb-1.5">
                    <div className="w-6 h-6 rounded-lg bg-rose-100 flex items-center justify-center">
                      <Zap className="w-3.5 h-3.5 text-rose-600" />
                    </div>
                    <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Swing Tempo</span>
                  </div>
                  {isEditing ? (
                    <select
                      value={formData.swingTempo}
                      onChange={(e) => handleInputChange('swingTempo', e.target.value)}
                      className="w-full px-3 py-2 bg-gray-50 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 font-medium"
                    >
                      <option value="slow">Slow</option>
                      <option value="medium">Medium</option>
                      <option value="fast">Fast</option>
                    </select>
                  ) : (
                    <p className="text-base font-semibold text-gray-900 capitalize mt-1">{formData.swingTempo}</p>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Golf Bag Management - Premium Card */}
      <div className="bg-white rounded-2xl shadow-lg overflow-hidden border border-gray-100">
        {/* Gradient Header */}
        <div className="bg-gradient-to-r from-emerald-600 via-green-600 to-teal-600 px-6 py-5">
          <div className="flex items-center justify-center space-x-3">
            <div className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center">
              <Package className="w-6 h-6 text-white" />
            </div>
            <div className="text-center">
              <h3 className="text-xl font-bold text-white">Manage Your Clubs</h3>
              <p className="text-green-100 text-sm">Clubs, distances, and equipment</p>
            </div>
          </div>
        </div>
        
        {/* Buttons */}
        <div className="p-5">
          <div className="grid grid-cols-2 gap-3">
            <Link
              to="/my-golf-bag"
              className="group flex items-center justify-center space-x-2 bg-gradient-to-r from-green-600 to-emerald-600 text-white px-4 py-3.5 rounded-xl hover:shadow-lg hover:shadow-green-500/30 hover:-translate-y-0.5 transition-all font-semibold"
            >
              <Package className="w-5 h-5 group-hover:scale-110 transition-transform" />
              <span>My Golf Bag</span>
            </Link>
            
            <Link
              to="/club-optimizer"
              className="group flex items-center justify-center space-x-2 bg-gradient-to-r from-purple-600 to-indigo-600 text-white px-4 py-3.5 rounded-xl hover:shadow-lg hover:shadow-purple-500/30 hover:-translate-y-0.5 transition-all font-semibold"
            >
              <Settings className="w-5 h-5 group-hover:rotate-90 transition-transform" />
              <span>Club Optimizer</span>
            </Link>
          </div>
        </div>
      </div>

      {/* OLD Golf Bag Section - REMOVED (moved to My Golf Bag) */}
      <div className="hidden">
        <div className="flex items-center justify-between mb-4 sm:mb-6">
          <h3 className="text-base sm:text-lg font-semibold text-gray-900 flex items-center">
            <MapPin className="w-4 h-4 sm:w-5 sm:h-5 text-blue-500 mr-2" />
            Golf Bag
          </h3>
          <button
            onClick={handleStartGolfBagEdit}
            className="flex items-center space-x-1 sm:space-x-2 px-3 sm:px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm sm:text-base"
          >
            <Settings className="w-4 h-4" />
            <span className="hidden sm:inline">Edit Bag</span>
            <span className="sm:hidden">Edit</span>
          </button>
        </div>

        {isEditingGolfBag ? (
          <div>
            <div style={{ marginBottom: "1.5rem", textAlign: "center" }}>
              <p style={{ color: "#6b7280", fontSize: "0.875rem", marginBottom: "0.5rem" }}>
                Select up to 13 clubs for your bag ({tempGolfBag.length}/13)
              </p>
              <p style={{ color: "#9ca3af", fontSize: "0.75rem" }}>
                Click clubs to add or remove them from your bag
              </p>
            </div>

            <div style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(140px, 1fr))",
              gap: "0.75rem"
            }}>
              {Object.entries(CLUB_DATA)
                .sort(([,a], [,b]) => a.order - b.order)
                .map(([clubType, clubData]) => {
                  const isInBag = tempGolfBag.some(club => club.type === clubType);
                  return (
                    <button
                      key={clubType}
                      onClick={() => toggleClubInBag(clubType)}
                      style={{
                        padding: "0.75rem",
                        border: `2px solid ${isInBag ? "#3b82f6" : "#e5e7eb"}`,
                        borderRadius: "0.5rem",
                        backgroundColor: isInBag ? "#eff6ff" : "#ffffff",
                        cursor: "pointer",
                        textAlign: "center",
                        transition: "all 0.2s",
                      }}
                    >
                      <div style={{ fontSize: "0.75rem", fontWeight: "600", color: isInBag ? "#1d4ed8" : "#374151" }}>
                        {clubType.toUpperCase().replace('-', ' ')}
                      </div>
                      <div style={{ fontSize: "0.75rem", color: isInBag ? "#1d4ed8" : "#6b7280" }}>
                        {clubData.loft}°
                      </div>
                    </button>
                  );
                })}
            </div>

            <div style={{ marginTop: "1.5rem", display: "flex", justifyContent: "center", gap: "0.5rem" }}>
              <button
                onClick={handleSaveGolfBag}
                style={{
                  padding: "0.75rem 1.5rem",
                  backgroundColor: "#10b981",
                  color: "white",
                  border: "none",
                  borderRadius: "0.5rem",
                  cursor: "pointer",
                  fontWeight: "500",
                }}
              >
                Save Bag
              </button>
              <button
                onClick={handleCancelGolfBag}
                style={{
                  padding: "0.75rem 1.5rem",
                  backgroundColor: "#6b7280",
                  color: "white",
                  border: "none",
                  borderRadius: "0.5rem",
                  cursor: "pointer",
                  fontWeight: "500",
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <div>
            {formData.golfBag && formData.golfBag.length > 0 ? (
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", gap: "1rem" }}>
                {formData.golfBag.map((club) => (
                  <div
                    key={club.id}
                    style={{
                      padding: "1rem",
                      border: "1px solid #e5e7eb",
                      borderRadius: "0.5rem",
                      backgroundColor: "#f9fafb",
                    }}
                  >
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "0.5rem" }}>
                      <h4 style={{ fontSize: "1rem", fontWeight: "600", color: "#1f2937" }}>
                        {club.type.charAt(0).toUpperCase() + club.type.slice(1).replace('-', ' ')}
                      </h4>
                    </div>
                    <div style={{ fontSize: "0.875rem", color: "#6b7280", marginBottom: "0.5rem" }}>
                      {club.brand} {club.model}
                    </div>
                    <div style={{ fontSize: "0.75rem", color: "#9ca3af", marginBottom: "0.5rem" }}>
                      {club.shaftType} • {club.shaftFlex}
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", marginBottom: "0.5rem" }}>
                      <span style={{ fontSize: "0.875rem", color: "#6b7280" }}>
                        Loft: {club.loft}°
                      </span>
                      <button
                        onClick={() => handleLoftEdit(club.id, club.loft)}
                        style={{
                          padding: "0.125rem 0.375rem",
                          backgroundColor: "#8b5cf6",
                          color: "white",
                          border: "none",
                          borderRadius: "0.25rem",
                          cursor: "pointer",
                          fontSize: "0.625rem",
                        }}
                      >
                        Edit
                      </button>
                    </div>
                    <button
                      onClick={() => handleClubDetailsEdit(club)}
                      style={{
                        padding: "0.375rem 0.75rem",
                        backgroundColor: "#10b981",
                        color: "white",
                        border: "none",
                        borderRadius: "0.25rem",
                        cursor: "pointer",
                        fontSize: "0.75rem",
                        width: "100%",
                        marginBottom: "0.5rem"
                      }}
                    >
                      Edit Details
                    </button>
                    {club.distance && (
                      <div style={{ fontSize: "1.25rem", fontWeight: "600", color: "#059669", marginBottom: "0.5rem" }}>
                        {club.distance} yds
                      </div>
                    )}
                    <button
                      onClick={() => handleDistanceEdit(club.id, club.distance)}
                      style={{
                        padding: "0.375rem 0.75rem",
                        backgroundColor: "#3b82f6",
                        color: "white",
                        border: "none",
                        borderRadius: "0.25rem",
                        cursor: "pointer",
                        fontSize: "0.75rem",
                        width: "100%",
                      }}
                    >
                      {club.distance ? "Edit" : "Add"} Distance
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <div style={{ textAlign: "center", padding: "3rem", color: "#6b7280" }}>
                <MapPin size={48} style={{ margin: "0 auto 1rem", opacity: 0.5 }} />
                <p style={{ fontSize: "1.125rem", fontWeight: "500", marginBottom: "0.5rem" }}>
                  No clubs in your bag yet
                </p>
                <p style={{ fontSize: "0.875rem" }}>
                  Click "Edit Bag" above to start building your golf bag
                </p>
              </div>
            )}
          </div>
        )}

        {/* Distance Edit Modal */}
        {editingClub && (
          <div style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
          }}>
            <div style={{
              backgroundColor: "white",
              padding: "2rem",
              borderRadius: "1rem",
              maxWidth: "400px",
              width: "90%",
            }}>
              <h3 style={{ fontSize: "1.25rem", fontWeight: "600", marginBottom: "1rem" }}>
                Edit Distance
              </h3>
              <input
                type="number"
                value={editDistance}
                onChange={(e) => setEditDistance(e.target.value)}
                placeholder="Enter distance in yards"
                style={{
                  width: "100%",
                  padding: "0.75rem",
                  border: "1px solid #d1d5db",
                  borderRadius: "0.5rem",
                  marginBottom: "1rem",
                  fontSize: "1rem",
                }}
              />
              <div style={{ display: "flex", gap: "0.5rem" }}>
                <button
                  onClick={saveDistanceEdit}
                  style={{
                    flex: 1,
                    padding: "0.75rem",
                    backgroundColor: "#10b981",
                    color: "white",
                    border: "none",
                    borderRadius: "0.5rem",
                    cursor: "pointer",
                  }}
                >
                  Save
                </button>
                <button
                  onClick={cancelDistanceEdit}
                  style={{
                    flex: 1,
                    padding: "0.75rem",
                    backgroundColor: "#6b7280",
                    color: "white",
                    border: "none",
                    borderRadius: "0.5rem",
                    cursor: "pointer",
                  }}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Loft Edit Modal */}
        {editingLoft && (
          <div style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.6)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
            padding: "1rem",
          }}
          onClick={(e) => {
            // Close modal if clicking backdrop
            if (e.target === e.currentTarget) {
              setEditingLoft(null);
              setEditLoftValue("");
            }
          }}
          >
            <div style={{
              backgroundColor: "white",
              padding: "2rem",
              borderRadius: "1rem",
              maxWidth: "400px",
              width: "90%",
            }}>
              <h3 style={{ fontSize: "1.25rem", fontWeight: "600", marginBottom: "1rem" }}>
                Edit Loft
              </h3>
              <input
                type="number"
                step="0.5"
                value={editLoftValue}
                onChange={(e) => setEditLoftValue(e.target.value)}
                placeholder="Enter loft in degrees"
                style={{
                  width: "100%",
                  padding: "0.75rem",
                  border: "1px solid #d1d5db",
                  borderRadius: "0.5rem",
                  marginBottom: "0.5rem",
                  fontSize: "1rem",
                }}
              />
              <p style={{ fontSize: "0.75rem", color: "#6b7280", marginBottom: "1rem" }}>
                Common lofts: PW (44-48°), GW (50-52°), SW (54-56°), LW (58-60°)
              </p>
              <div style={{ display: "flex", gap: "0.5rem" }}>
                <button
                  onClick={saveLoftEdit}
                  style={{
                    flex: 1,
                    padding: "0.75rem",
                    backgroundColor: "#8b5cf6",
                    color: "white",
                    border: "none",
                    borderRadius: "0.5rem",
                    cursor: "pointer",
                  }}
                >
                  Save
                </button>
                <button
                  onClick={cancelLoftEdit}
                  style={{
                    flex: 1,
                    padding: "0.75rem",
                    backgroundColor: "#6b7280",
                    color: "white",
                    border: "none",
                    borderRadius: "0.5rem",
                    cursor: "pointer",
                  }}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Club Details Edit Modal */}
        {editingClubDetails && (
          <div style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.6)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
            padding: "1rem",
          }}
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              cancelClubDetailsEdit();
            }
          }}
          >
            <div style={{
              backgroundColor: "white",
              padding: "2rem",
              borderRadius: "1rem",
              maxWidth: "500px",
              width: "90%",
              maxHeight: "90vh",
              overflowY: "auto",
            }}>
              <h3 style={{ fontSize: "1.25rem", fontWeight: "600", marginBottom: "1.5rem" }}>
                Edit Club Details
              </h3>
              
              <div style={{ marginBottom: "1rem" }}>
                <label style={{ display: "block", fontSize: "0.875rem", fontWeight: "500", marginBottom: "0.5rem" }}>
                  Brand
                </label>
                <input
                  type="text"
                  value={editClubBrand}
                  onChange={(e) => setEditClubBrand(e.target.value)}
                  placeholder="e.g., Titleist, Callaway"
                  style={{
                    width: "100%",
                    padding: "0.75rem",
                    border: "1px solid #d1d5db",
                    borderRadius: "0.5rem",
                    fontSize: "1rem",
                  }}
                />
              </div>

              <div style={{ marginBottom: "1rem" }}>
                <label style={{ display: "block", fontSize: "0.875rem", fontWeight: "500", marginBottom: "0.5rem" }}>
                  Model
                </label>
                <input
                  type="text"
                  value={editClubModel}
                  onChange={(e) => setEditClubModel(e.target.value)}
                  placeholder="e.g., AP2, Rogue"
                  style={{
                    width: "100%",
                    padding: "0.75rem",
                    border: "1px solid #d1d5db",
                    borderRadius: "0.5rem",
                    fontSize: "1rem",
                  }}
                />
              </div>

              <div style={{ marginBottom: "1rem" }}>
                <label style={{ display: "block", fontSize: "0.875rem", fontWeight: "500", marginBottom: "0.5rem" }}>
                  Shaft Type
                </label>
                <select
                  value={editClubShaftType}
                  onChange={(e) => setEditClubShaftType(e.target.value)}
                  style={{
                    width: "100%",
                    padding: "0.75rem",
                    border: "1px solid #d1d5db",
                    borderRadius: "0.5rem",
                    fontSize: "1rem",
                  }}
                >
                  <option value="steel">Steel</option>
                  <option value="graphite">Graphite</option>
                  <option value="hybrid">Hybrid</option>
                </select>
              </div>

              <div style={{ marginBottom: "1rem" }}>
                <label style={{ display: "block", fontSize: "0.875rem", fontWeight: "500", marginBottom: "0.5rem" }}>
                  Shaft Flex
                </label>
                <select
                  value={editClubShaftFlex}
                  onChange={(e) => setEditClubShaftFlex(e.target.value)}
                  style={{
                    width: "100%",
                    padding: "0.75rem",
                    border: "1px solid #d1d5db",
                    borderRadius: "0.5rem",
                    fontSize: "1rem",
                  }}
                >
                  <option value="ladies">Ladies (L)</option>
                  <option value="senior">Senior (A)</option>
                  <option value="regular">Regular (R)</option>
                  <option value="stiff">Stiff (S)</option>
                  <option value="extra-stiff">Extra Stiff (X)</option>
                </select>
              </div>

              <div style={{ marginBottom: "1.5rem" }}>
                <label style={{ display: "block", fontSize: "0.875rem", fontWeight: "500", marginBottom: "0.5rem" }}>
                  Grip Type
                </label>
                <select
                  value={editClubGripType}
                  onChange={(e) => setEditClubGripType(e.target.value)}
                  style={{
                    width: "100%",
                    padding: "0.75rem",
                    border: "1px solid #d1d5db",
                    borderRadius: "0.5rem",
                    fontSize: "1rem",
                  }}
                >
                  <option value="standard">Standard</option>
                  <option value="midsize">Midsize</option>
                  <option value="jumbo">Jumbo</option>
                  <option value="undersize">Undersize</option>
                  <option value="corded">Corded</option>
                  <option value="wrap">Wrap</option>
                </select>
              </div>

              <div style={{ display: "flex", gap: "0.5rem" }}>
                <button
                  onClick={saveClubDetailsEdit}
                  style={{
                    flex: 1,
                    padding: "0.75rem",
                    backgroundColor: "#10b981",
                    color: "white",
                    border: "none",
                    borderRadius: "0.5rem",
                    cursor: "pointer",
                    fontWeight: "500",
                  }}
                >
                  Save Details
                </button>
                <button
                  onClick={cancelClubDetailsEdit}
                  style={{
                    flex: 1,
                    padding: "0.75rem",
                    backgroundColor: "#6b7280",
                    color: "white",
                    border: "none",
                    borderRadius: "0.5rem",
                    cursor: "pointer",
                    fontWeight: "500",
                  }}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Practice Recommendations */}
      {practiceRecommendations.length > 0 && (
        <div className="card p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <Lightbulb className="w-5 h-5 text-yellow-500 mr-2" />
            Practice Recommendations
          </h3>
          <div className="space-y-4">
            {practiceRecommendations.map((rec, index) => (
              <div key={index} className="p-4 bg-yellow-50 rounded-lg border border-yellow-200">
                <h4 className="font-semibold text-gray-900 mb-2">{rec.title}</h4>
                <p className="text-gray-700 text-sm">{rec.description}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Dashboard Elements Section - BELOW My Golf Bag and Club Optimizer */}
      <div className="mt-8 space-y-6">
        {/* AI Daily Tip */}
        {dailyTip && (
          <div className="card p-6 bg-gradient-to-r from-green-50 to-blue-50 border-2 border-green-200">
            <div className="flex items-center mb-4">
              <Zap className="w-6 h-6 text-green-600 mr-2" />
              <h3 className="text-xl font-bold text-gray-900">Today's AI Tip</h3>
            </div>
            <div className="bg-white rounded-lg p-4 border border-green-100">
              <p className="text-gray-800 leading-relaxed">{stripMarkdown(dailyTip)}</p>
            </div>
          </div>
        )}

        {/* Referral Card - Driver Giveaway */}
        {referralCode && (
          <div className="card p-6 bg-gradient-to-r from-amber-50 via-orange-50 to-yellow-50 border-2 border-amber-300 shadow-lg">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center">
                <Award className="w-7 h-7 text-amber-600 mr-3" />
                <div>
                  <h3 className="text-xl font-bold text-gray-900">🎁 Driver Giveaway</h3>
                  <p className="text-sm text-amber-700">Refer friends for bonus entries!</p>
                </div>
              </div>
              {referralCount > 0 && (
                <div className="bg-amber-500 text-white px-4 py-2 rounded-full text-sm font-bold shadow-md">
                  {referralCount + 1} {referralCount === 0 ? 'Entry' : 'Entries'}
                </div>
              )}
            </div>
            
            {/* Referral Code Display */}
            <div className="bg-white rounded-xl p-4 mb-4 border-2 border-amber-200 shadow-inner">
              <div className="text-xs text-gray-500 uppercase tracking-wider mb-1">Your Referral Code</div>
              <div className="flex items-center justify-between">
                <div className="text-3xl font-black tracking-widest text-amber-600 font-mono">
                  {referralCode}
                </div>
                <button
                  onClick={async () => {
                    try {
                      await navigator.clipboard.writeText(referralCode);
                      setReferralCopied(true);
                      setTimeout(() => setReferralCopied(false), 2000);
                    } catch (err) {
                      console.error('Failed to copy:', err);
                    }
                  }}
                  className={`px-4 py-2 rounded-lg font-semibold text-sm transition-all ${
                    referralCopied 
                      ? 'bg-green-500 text-white' 
                      : 'bg-amber-500 hover:bg-amber-600 text-white'
                  }`}
                >
                  {referralCopied ? '✓ Copied!' : 'Copy Code'}
                </button>
              </div>
            </div>

            {/* Share Buttons - 2x2 grid on mobile, 4 in row on desktop */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 mb-4">
              <button
                onClick={() => {
                  const text = `🏌️ I'm using ClubIQ to improve my golf game! Use my code ${referralCode} when you sign up for bonus giveaway entries. Try it free:`;
                  const url = `${window.location.origin}?ref=${referralCode}`;
                  window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`, '_blank');
                }}
                className="flex items-center justify-center gap-2 px-3 py-3 bg-black hover:bg-gray-900 text-white rounded-xl font-semibold text-sm transition-all shadow-lg hover:shadow-xl hover:-translate-y-0.5"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
                <span>Share</span>
              </button>
              <button
                onClick={() => {
                  const url = `${window.location.origin}?ref=${referralCode}`;
                  window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`, '_blank');
                }}
                className="flex items-center justify-center gap-2 px-3 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-semibold text-sm transition-all shadow-lg hover:shadow-xl hover:-translate-y-0.5"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
                <span>Share</span>
              </button>
              <button
                onClick={() => {
                  const url = `${window.location.origin}?ref=${referralCode}`;
                  const text = `🏌️ Hey! I'm using ClubIQ to improve my golf game. Use my code ${referralCode} when you sign up for bonus giveaway entries! ${url}`;
                  // SMS link works on both iOS and Android
                  const smsUrl = `sms:?&body=${encodeURIComponent(text)}`;
                  window.location.href = smsUrl;
                }}
                className="flex items-center justify-center gap-2 px-3 py-3 bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 text-white rounded-xl font-semibold text-sm transition-all shadow-lg hover:shadow-xl hover:-translate-y-0.5"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                </svg>
                <span>Text</span>
              </button>
              <button
                onClick={async () => {
                  const url = `${window.location.origin}?ref=${referralCode}`;
                  const text = `🏌️ Use my ClubIQ referral code ${referralCode} for bonus giveaway entries! ${url}`;
                  try {
                    await navigator.clipboard.writeText(text);
                    alert('Link copied! Share it with your golf buddies!');
                  } catch (err) {
                    console.error('Failed to copy:', err);
                  }
                }}
                className="flex items-center justify-center gap-2 px-3 py-3 bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white rounded-xl font-semibold text-sm transition-all shadow-lg hover:shadow-xl hover:-translate-y-0.5"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3"/></svg>
                <span>Copy</span>
              </button>
            </div>

            {/* How it works */}
            <div className="bg-white/60 rounded-lg p-3 text-sm text-gray-700">
              <div className="font-semibold mb-1">How it works:</div>
              <ul className="space-y-1 text-xs">
                <li>• You automatically have 1 entry in our Driver Giveaway</li>
                <li>• Each friend who signs up with your code = +1 bonus entry for you</li>
                <li>• Your friends also get their code to share!</li>
              </ul>
            </div>
          </div>
        )}

        {/* Weather, Pro Tips, and ClubIQ Journey Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
          {/* Current Weather */}
          {weather && (
            <div className="card p-6 bg-gradient-to-br from-blue-50 to-indigo-50 border-2 border-blue-100">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <Cloud className="w-5 h-5 text-blue-600 mr-2" />
                Current Conditions
              </h3>
              
              <div className="mb-4">
                <button
                  onClick={async () => {
                    setIsLoadingLocation(true);
                    try {
                      const location = await getUserLocation();
                      const weatherData = await getCurrentWeather(location.lat, location.lon);
                      setWeather(weatherData);
                      setWeatherRecommendations(getWeatherRecommendations(weatherData));
                    } catch (error) {
                      console.error('Failed to get location:', error);
                    } finally {
                      setIsLoadingLocation(false);
                    }
                  }}
                  disabled={isLoadingLocation}
                  className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm w-full justify-center"
                >
                  <MapPin size={16} />
                  <span>{isLoadingLocation ? "Getting Location..." : "Use My Location"}</span>
                </button>
              </div>

              {weather.available ? (
              <div className="space-y-3">
                <p className="text-xs text-blue-700">Live conditions for your selected location</p>
                <div className="flex items-center justify-between p-3 bg-white rounded-lg">
                  <div className="flex items-center space-x-2">
                    <Thermometer size={18} className="text-red-500" />
                    <span className="text-sm text-gray-600 font-medium">Temperature</span>
                  </div>
                  <span className="font-bold text-gray-900">{weather.temperature}°F</span>
                </div>

                <div className="flex items-center justify-between p-3 bg-white rounded-lg">
                  <div className="flex items-center space-x-2">
                    <Wind size={18} className="text-blue-500" />
                    <span className="text-sm text-gray-600 font-medium">Wind</span>
                  </div>
                  <span className="font-bold text-gray-900">{weather.windSpeed} mph {weather.windDirection}</span>
                </div>

                <div className="flex items-center justify-between p-3 bg-white rounded-lg">
                  <span className="text-sm text-gray-600 font-medium">Humidity</span>
                  <span className="font-bold text-gray-900">{weather.humidity}%</span>
                </div>
              </div>
              ) : (
                <p className="text-sm text-gray-600 bg-white rounded-lg p-3">
                  Live weather is unavailable. ClubIQ will not show placeholder conditions as yours.
                  Use your location to load current conditions.
                </p>
              )}

              {weatherRecommendations.length > 0 && (
                <div className="mt-4 p-4 bg-blue-50 rounded-lg border border-blue-100">
                  <h4 className="font-semibold text-gray-900 mb-2 text-sm">🎯 Playing Tips</h4>
                  <div className="space-y-1">
                    {weatherRecommendations.slice(0, 2).map((rec, index) => (
                      <p key={index} className="text-sm text-gray-700 flex items-start gap-2">
                        <span className="text-blue-500">•</span>
                        <span>{rec}</span>
                      </p>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Pro Tips with Social Sharing */}
          <div className="card p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Pro Golf Tips</h3>
              <div className="text-xs text-green-600 font-medium bg-green-100 px-2 py-1 rounded-full">
                {proTips.length} of {baseTips.length + premiumTips.length} Unlocked
              </div>
            </div>
            
            {proTips.length > 0 ? (
              <>
                <div className="mb-4">
                  <div className="bg-gradient-to-r from-blue-50 to-green-50 rounded-lg p-4 border border-blue-100">
                    <div className="flex items-start space-x-3">
                      <div className="w-3 h-3 bg-green-500 rounded-full mt-1.5 flex-shrink-0"></div>
                      <div className="flex-1">
                        <div className="font-semibold text-gray-900 text-sm mb-1">
                          {proTips[currentTipIndex]?.title}
                        </div>
                        <p className="text-gray-700 text-sm">
                          {proTips[currentTipIndex]?.description}
                        </p>
                      </div>
                    </div>
                  </div>
                  
                  {proTips.length > 1 && (
                    <div className="flex items-center justify-between mt-3">
                      <button
                        onClick={() => setCurrentTipIndex((prev) => prev === 0 ? proTips.length - 1 : prev - 1)}
                        className="text-gray-400 hover:text-gray-600 text-sm"
                      >
                        ← Previous
                      </button>
                      <div className="text-xs text-gray-500">
                        {currentTipIndex + 1} of {proTips.length}
                      </div>
                      <button
                        onClick={() => setCurrentTipIndex((prev) => prev === proTips.length - 1 ? 0 : prev + 1)}
                        className="text-gray-400 hover:text-gray-600 text-sm"
                      >
                        Next →
                      </button>
                    </div>
                  )}
                </div>

                <div className="border-t border-gray-200 pt-4">
                  <div className="text-center">
                    <div className="text-sm font-medium text-gray-900 mb-2">
                      Unlock More Tips by Sharing
                    </div>
                    <div className="text-xs text-gray-500 mb-3">
                      Share ClubIQ with friends to unlock {premiumTips.length} premium golf tips
                    </div>
                    
                    <div className="flex justify-center space-x-2">
                      <button
                        onClick={async () => {
                          await shareToTwitter();
                          const tips = getAvailableTips();
                          setProTips(tips.filter(tip => tip.isUnlocked));
                          setShareCount(getShareCount());
                        }}
                        className="bg-blue-500 text-white px-3 py-1.5 rounded-md hover:bg-blue-600 transition-colors text-xs font-medium"
                      >
                        Twitter
                      </button>
                      <button
                        onClick={async () => {
                          await shareToFacebook();
                          const tips = getAvailableTips();
                          setProTips(tips.filter(tip => tip.isUnlocked));
                          setShareCount(getShareCount());
                        }}
                        className="bg-blue-600 text-white px-3 py-1.5 rounded-md hover:bg-blue-700 transition-colors text-xs font-medium"
                      >
                        Facebook
                      </button>
                      <button
                        onClick={async () => {
                          const success = await copyToClipboard();
                          if (success) {
                            alert("Link copied! Share it with friends to unlock more tips.");
                            const tips = getAvailableTips();
                            setProTips(tips.filter(tip => tip.isUnlocked));
                            setShareCount(getShareCount());
                          }
                        }}
                        className="bg-gray-600 text-white px-3 py-1.5 rounded-md hover:bg-gray-700 transition-colors text-xs font-medium"
                      >
                        Copy Link
                      </button>
                    </div>
                    
                    {getNextTipToUnlock() && (
                      <div className="mt-3 text-xs text-gray-500">
                        Next unlock: "{getNextTipToUnlock()?.title}" 
                        <span className="text-green-600 font-medium">
                          ({shareCount + 1} share{shareCount === 0 ? '' : 's'} needed)
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </>
            ) : (
              <div className="text-center py-4">
                <p className="text-sm text-gray-600">Share ClubIQ to unlock tips!</p>
              </div>
            )}
          </div>

          {/* ClubIQ Journey */}
          <div className="card p-6 bg-gradient-to-br from-indigo-50 to-purple-50 border-2 border-indigo-100">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold text-gray-900 flex items-center">
                <Target className="w-6 h-6 text-indigo-600 mr-2" />
                Your ClubIQ Journey
              </h3>
              <div className="text-xs text-indigo-600 font-medium bg-indigo-100 px-3 py-1 rounded-full whitespace-nowrap">
                {[
                  userProfile?.hasCompletedOnboarding,
                  (userProfile?.golfBag?.length || 0) >= 5,
                  false
                ].filter(Boolean).length}/3 Complete
              </div>
            </div>
            
            <div className="space-y-4">
              {/* Step 1: Onboarding */}
              <div className="bg-white rounded-lg p-3 border border-gray-100 hover:shadow-sm transition-all">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div
                      className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${
                        userProfile?.hasCompletedOnboarding
                          ? "bg-green-500 text-white"
                          : "bg-indigo-500 text-white"
                      }`}
                    >
                      {userProfile?.hasCompletedOnboarding ? "✓" : "1"}
                    </div>
                    <div>
                      <div className={`font-semibold text-sm ${userProfile?.hasCompletedOnboarding ? "text-green-700" : "text-gray-900"}`}>
                        {userProfile?.hasCompletedOnboarding ? "✅ Profile Complete" : "Complete Your Golf Profile"}
                      </div>
                      <div className="text-xs text-gray-500">
                        {userProfile?.hasCompletedOnboarding ? `${ONBOARDING_QUESTION_COUNT} questions answered` : `Quick ${ONBOARDING_QUESTION_COUNT}-question setup`}
                      </div>
                    </div>
                  </div>
                  {onOpenOnboarding && (
                    <button
                      onClick={onOpenOnboarding}
                      className={`px-3 py-1.5 rounded-md text-xs font-semibold min-w-[65px] ${
                        userProfile?.hasCompletedOnboarding
                          ? "bg-green-600 text-white hover:bg-green-700"
                          : "bg-indigo-600 text-white hover:bg-indigo-700"
                      } transition-colors`}
                    >
                      {userProfile?.hasCompletedOnboarding ? "Retake" : "Start"}
                    </button>
                  )}
                </div>
              </div>

              {/* Step 2: My Golf Bag */}
              <div className="bg-white rounded-lg p-3 border border-gray-100 hover:shadow-sm transition-all">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div
                      className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${
                        (userProfile?.golfBag?.length || 0) >= 5
                          ? "bg-green-500 text-white"
                          : (userProfile?.golfBag?.length || 0) > 0
                          ? "bg-yellow-500 text-white"
                          : "bg-gray-400 text-white"
                      }`}
                    >
                      {(userProfile?.golfBag?.length || 0) >= 5 ? "✓" : "2"}
                    </div>
                    <div>
                      <div className={`font-semibold text-sm ${(userProfile?.golfBag?.length || 0) >= 5 ? "text-gray-500 line-through" : "text-gray-900"}`}>
                        Set Up My Golf Bag
                      </div>
                      <div className="text-xs text-gray-500">
                        {userProfile?.golfBag?.length || 0}/14 clubs + distances
                      </div>
                    </div>
                  </div>
                  <Link
                    to="/my-golf-bag"
                    className="inline-block bg-green-600 text-white px-3 py-1.5 rounded-md hover:bg-green-700 transition-colors text-xs font-semibold min-w-[65px] text-center"
                  >
                    Open
                  </Link>
                </div>
              </div>

              {/* Step 3: Optimize Setup */}
              <div className="bg-white rounded-lg p-3 border border-gray-100 hover:shadow-sm transition-all">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className="w-7 h-7 rounded-full bg-purple-500 text-white flex items-center justify-center text-xs font-bold">
                      3
                    </div>
                    <div>
                      <div className="font-semibold text-sm text-gray-900">
                        Optimize Your Equipment
                      </div>
                      <div className="text-xs text-gray-500">
                        Gapping analysis & recommendations
                      </div>
                    </div>
                  </div>
                  <Link
                    to="/club-optimizer"
                    className="inline-block bg-purple-600 text-white px-3 py-1.5 rounded-md hover:bg-purple-700 transition-colors text-xs font-semibold min-w-[65px] text-center"
                  >
                    Optimize
                  </Link>
                </div>
              </div>
            </div>
          </div>

          <AccountPrivacy onSignOut={onSignOut} />
        </div>
      </div>

    </div>
    </>
  );
};

export default UserProfile;